<template>
  <div class="d-grid gap-3">
    <h3 class="my-0">
      {{ i18n.t('local_chapters.join.about_you') }}
    </h3>

    <text-input v-for="question in questions" :key="question.slug" multiline
                :input-id="`group-member-question-${question.slug}`"
                :value="newMember.newMemberQuestionResponses[question.slug]"
                :label="question.label" :help-text="question.help_text"
                :validation-errors="validationErrors[`new_member_question_responses.${question.slug}`]"
                @changed="(v) => updateQuestionResponse(question.slug, v)" />

    <additional-field v-for="field of additionalFields" v-show="field.kind !== 'hidden'" :key="field.name"
                      v-bind="additionalFieldProps(field)" :validation-errors="validationErrors[field.name]"
                      @changed="(v) => updateAdditionalField(field.name, v)" />

    <radio-buttons-input :initial-value="newMember.notificationLevel" :options="notificationLevelOptions"
                         :label="i18n.t('local_chapters.members.form.notification_level')"
                         input-name="group_member[notification_level]"
                         :validation-errors="validationErrors.notification_level"
                         @change="updateNotificationLevel" />

    <eu-data-processing-consent v-if="dataProcessingConsentProps.enabled"
                                entity-name="group_member"
                                :explicit-consent-required="dataProcessingConsentProps.explicitConsentRequired"
                                :label="dataProcessingConsentProps.label"
                                :show-label="showDataProcessingConsentLabelBeforeSubmit"
                                :validation-errors="validationErrors.eu_data_processing_consent"
                                @changed="updateDataProcessingConsent" />

    <div v-if="showDisclaimerBeforeSubmit" v-html="disclaimerContent.beforeSubmit" />

    <submit-button :label="submitButtonLabel" :submit-in-progress="submitting" @click="$emit('submit')" />

    <div v-if="showDataProcessingConsentLabelAfterSubmit" class="small-text mt-2"
         v-html="dataProcessingConsentProps.label" />
  </div>
</template>

<script>
import AdditionalField from '@/components/inputs/additional-field'
import EuDataProcessingConsent from '@/components/inputs/eu-data-processing-consent'
import { mapMutations, mapState } from 'vuex'
import RadioButtonsInput from '@/components/inputs/radio-buttons-input'
import SubmitButton from '@/components/buttons/submit-button'
import TextInput from '@/components/inputs/text-input'

export default {
  components: { AdditionalField, EuDataProcessingConsent, RadioButtonsInput, SubmitButton, TextInput },

  props: {
    submitButtonLabel: {
      type: String,
      required: true
    },

    submitting: {
      type: Boolean,
      required: true
    }
  },

  emits: ['submit'],

  computed: {
    ...mapState({
      additionalFields: 'joinAdditionalFields',
      dataProcessingConsentProps: 'dataProcessingConsentProps',
      disclaimerContent: 'joinDisclaimerContent',
      questions: 'joinQuestions',
      newMember: 'newMember',
      notificationLevelOptions: 'notificationLevelOptions'
    }),

    showDataProcessingConsentLabelAfterSubmit() {
      return this.dataProcessingConsentProps.enabled &&
             !this.dataProcessingConsentProps.explicitConsentRequired &&
             this.dataProcessingConsentProps.messagePosition === 'after_submit'
    },

    showDataProcessingConsentLabelBeforeSubmit() {
      return this.dataProcessingConsentProps.enabled &&
             this.dataProcessingConsentProps.explicitConsentRequired ||
             this.dataProcessingConsentProps.messagePosition === 'before_submit'
    },

    showDisclaimerBeforeSubmit() {
      return this.disclaimerContent.beforeSubmit.length > 0
    },

    validationErrors() {
      return this.newMember.validationErrors
    }
  },

  methods: {
    ...mapMutations(['updateNewMemberAdditionalField', 'updateNewMemberField', 'updateNewMemberQuestionResponse']),

    additionalFieldProps(fieldConfig) {
      const fieldName = fieldConfig.name
      const currentValue = this.additionalFieldValue(fieldConfig)

      return {
        entityName: 'group_member',
        fieldName,
        initialValue: currentValue,
        id: `additional-field-${fieldName}`,
        ...fieldConfig,
        ...fieldConfig.validations
      }
    },

    additionalFieldValue(fieldConfig) {
      return this.newMember.additionalFieldValues[fieldConfig.name]
    },

    updateAdditionalField(fieldName, newValue) {
      this.updateNewMemberAdditionalField({ field: fieldName, value: newValue })
    },

    updateDataProcessingConsent(newValue) {
      this.updateNewMemberField({ field: 'dataProcessingConsent', value: newValue })
    },

    updateQuestionResponse(questionSlug, newValue) {
      this.updateNewMemberQuestionResponse({ slug: questionSlug, value: newValue })
    },

    updateNotificationLevel(newValue) {
      this.updateNewMemberField({ field: 'notificationLevel', value: newValue })
    }
  }
}
</script>
